/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Content,
  GoogleGenAI,
  LiveCallbacks,
  LiveClientToolResponse,
  LiveConnectConfig,
  LiveServerContent,
  LiveServerMessage,
  LiveServerToolCall,
  LiveServerToolCallCancellation,
  Part,
  Session,
} from "@google/genai";

import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import { LiveClientOptions, StreamingLog } from "../types";
import { base64ToArrayBuffer } from "./utils";

/**
 * Event types that can be emitted by the MultimodalLiveClient.
 * Each event corresponds to a specific message from GenAI or client state change.
 */
export interface LiveClientEventTypes {
  // Emitted when audio data is received
  audio: (data: ArrayBuffer) => void;
  // Emitted when the connection closes
  close: (event: CloseEvent) => void;
  // Emitted when content is received from the server
  content: (data: LiveServerContent) => void;
  // Emitted when an error occurs
  error: (error: ErrorEvent) => void;
  // Emitted when the server interrupts the current generation
  interrupted: () => void;
  // Emitted for logging events
  log: (log: StreamingLog) => void;
  // Emitted when the connection opens
  open: () => void;
  // Emitted when the initial setup is complete
  setupcomplete: () => void;
  // Emitted when a tool call is received
  toolcall: (toolCall: LiveServerToolCall) => void;
  // Emitted when a tool call is cancelled
  toolcallcancellation: (
    toolcallCancellation: LiveServerToolCallCancellation
  ) => void;
  // Emitted when the current turn is complete
  turncomplete: () => void;
}

// Global counter for unique client IDs
let clientIdCounter = 0;

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 * If you dont want to use react you can still use this.
 */
export class GenAILiveClient extends EventEmitter<LiveClientEventTypes> {
  protected client: GoogleGenAI;
  
  // Unique client ID for debugging - helps track which client instance is active
  public readonly clientId: string;

  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  private _customWebSocket: WebSocket | null = null; // For ephemeral token connections
  
  public get status() {
    return this._status;
  }

  private _session: Session | null = null;
  public get session() {
    return this._session;
  }

  private _model: string | null = null;
  public get model() {
    return this._model;
  }

  protected config: LiveConnectConfig | null = null;

  public getConfig() {
    return { ...this.config };
  }

  constructor(options: LiveClientOptions) {
    super();
    
    // Assign unique client ID for debugging
    clientIdCounter++;
    this.clientId = `client-${clientIdCounter}-${Date.now()}`;
    console.log(`🆔 [CLIENT] Created new GenAILiveClient with ID: ${this.clientId}`);
    
    // Support both apiKey and token authentication
    // Token-based auth is preferred for security (locks config on backend)
    if (options.token) {
      console.log(`🔑 [GEMINI] [${this.clientId}] Initializing with ephemeral token (secure mode)`);
      // When using ephemeral tokens, config is locked on the backend
      // MUST use v1alpha API version for ephemeral token authentication
      this.client = new GoogleGenAI({ 
        apiKey: options.token,
        apiVersion: 'v1alpha'
      });
    } else if (options.apiKey) {
      console.log(`🔑 [GEMINI] [${this.clientId}] Initializing with API key (legacy mode)`);
      this.client = new GoogleGenAI(options);
    } else {
      throw new Error("Either apiKey or token must be provided for authentication");
    }
    
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(model: string, config: LiveConnectConfig): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      console.log("🔗 [GEMINI] Connection already in progress or connected, skipping");
      return false;
    }

    console.log("🚀 [GEMINI] Initiating connection to Gemini Live API");
    console.log("📋 [GEMINI] Model:", model);
    console.log("⚙️ [GEMINI] Config:", JSON.stringify(config, null, 2));

    this._status = "connecting";
    this.config = config;
    this._model = model;

    // Check if we're using an ephemeral token
    const apiKey = (this.client as any).apiClient?.getApiKey();
    const isEphemeralToken = apiKey && apiKey.startsWith('auth_tokens/');

    if (isEphemeralToken) {
      console.log("🔑 [GEMINI] Detected ephemeral token - using custom WebSocket with access_token parameter");
      
      // Build WebSocket URL with access_token for ephemeral tokens
      // Use BidiGenerateContentConstrained endpoint for tokens with liveConnectConstraints
      const websocketBaseUrl = 'wss://generativelanguage.googleapis.com';
      const apiVersion = 'v1alpha';
      const url = `${websocketBaseUrl}/ws/google.ai.generativelanguage.${apiVersion}.GenerativeService.BidiGenerateContentConstrained?access_token=${apiKey}`;
      
      console.log("🔗 [GEMINI] Connecting to:", url.replace(apiKey!, '[TOKEN_HIDDEN]'));
      
      // Create WebSocket connection manually for ephemeral tokens
      return this.connectWithCustomWebSocket(url, model, config);
    } else {
      // Use normal SDK method for regular API keys
      const callbacks: LiveCallbacks = {
        onopen: this.onopen,
        onmessage: this.onmessage,
        onerror: this.onerror,
        onclose: this.onclose,
      };

      try {
        console.log("🔄 [GEMINI] Attempting to establish connection with SDK...");
        this._session = await this.client.live.connect({
          model,
          config,
          callbacks,
        });
        console.log("✅ [GEMINI] Successfully connected to Gemini Live API");
      } catch (e) {
        console.error("❌ [GEMINI] Error connecting to GenAI Live:", e);
        this._status = "disconnected";
        return false;
      }

      this._status = "connected";
      return true;
    }
  }

  private async connectWithCustomWebSocket(
    url: string,
    model: string,
    config: LiveConnectConfig
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      console.log("🔌 [GEMINI] Creating custom WebSocket connection for ephemeral token");
      
      const ws = new WebSocket(url);
      this._customWebSocket = ws;
      
      ws.onopen = () => {
        console.log("✅ [GEMINI] Custom WebSocket connection opened");
        
        // For ephemeral tokens with liveConnectConstraints, send empty setup
        // All configuration (model, system_instruction, tools, response_modalities, speech_config)
        // is already locked in the token on the backend
        const setupMessage = {
          setup: {}
        };
        
        console.log("📤 [GEMINI] Sending empty setup message (everything locked in ephemeral token):", JSON.stringify(setupMessage, null, 2));
        ws.send(JSON.stringify(setupMessage));
        
        this._status = "connected";
        this.onopen();
        resolve(true);
      };
      
      ws.onmessage = async (event) => {
        try {
          let messageText: string;
          
          // Handle both text and Blob messages
          if (event.data instanceof Blob) {
            // console.log("📥 [GEMINI] Received Blob message, converting to text...");
            messageText = await event.data.text();
          } else {
            messageText = event.data;
          }
          
          const data = JSON.parse(messageText);
          // console.log("📥 [GEMINI] Received message:", data);
          
          // Convert WebSocket message to SDK format and call onmessage
          this.onmessage(data as LiveServerMessage);
        } catch (e) {
          console.error("❌ [GEMINI] Error parsing message:", e);
        }
      };
      
      ws.onerror = (event) => {
        console.error("❌ [GEMINI] Custom WebSocket error:", event);
        this._status = "disconnected";
        this.onerror(event as any);
        reject(new Error("Custom WebSocket connection failed"));
      };
      
      ws.onclose = (event) => {
        console.log("🔒 [GEMINI] Custom WebSocket connection closed");
        console.log("📋 [GEMINI] Close code:", event.code, "Reason:", event.reason);
        this._status = "disconnected";
        this._customWebSocket = null;
        this.onclose(event);
      };
    });
  }

  public disconnect() {
    // Handle custom WebSocket for ephemeral tokens
    if (this._customWebSocket) {
      console.log("🔌 [GEMINI] Disconnecting custom WebSocket for ephemeral token");
      if (this._customWebSocket.readyState === WebSocket.OPEN) {
        this._customWebSocket.close();
      }
      this._customWebSocket = null;
      this._status = "disconnected";
      this.log("client.close", `Disconnected custom WebSocket`);
      console.log("✅ [GEMINI] Successfully disconnected custom WebSocket");
      return true;
    }
    
    // Handle normal SDK session
    if (!this.session) {
      console.log("🔌 [GEMINI] No active session to disconnect");
      return false;
    }
    console.log("🔌 [GEMINI] Disconnecting from Gemini Live API");
    this.session?.close();
    this._session = null;
    this._status = "disconnected";

    this.log("client.close", `Disconnected`);
    console.log("✅ [GEMINI] Successfully disconnected from Gemini Live API");
    return true;
  }

  protected onopen() {
    console.log("🔓 [GEMINI] WebSocket connection opened with Gemini Live API");
    this.log("client.open", "Connected");
    this.emit("open");
  }

  protected onerror(e: ErrorEvent) {
    console.error("❌ [GEMINI] WebSocket error from Gemini Live API:", e.message);
    this.log("server.error", e.message);
    this.emit("error", e);
  }

  protected onclose(e: CloseEvent) {
    console.log("🔒 [GEMINI] WebSocket connection closed with Gemini Live API", e.reason ? `Reason: ${e.reason}` : "");
    this.log(
      `server.close`,
      `disconnected ${e.reason ? `with reason: ${e.reason}` : ``}`
    );
    this.emit("close", e);
  }

  protected async onmessage(message: LiveServerMessage) {
    // console.log("📨 [GEMINI] Received message from Gemini Live API:", message);
    
    if (message.setupComplete) {
      console.log("✅ [GEMINI] Setup completed with Gemini Live API");
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }
    if (message.toolCall) {
      console.log(`🔧 [GEMINI] [${this.clientId}] Tool call received from Gemini:`, message.toolCall);
      console.log(`🔧 [GEMINI] [${this.clientId}] Function calls:`, message.toolCall.functionCalls?.map(fc => fc.name));
      this.log("server.toolCall", message);
      this.emit("toolcall", message.toolCall);
      return;
    }
    if (message.toolCallCancellation) {
      console.log("❌ [GEMINI] Tool call cancellation received from Gemini:", message.toolCallCancellation);
      this.log("server.toolCallCancellation", message);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    // this json also might be `contentUpdate { interrupted: true }`
    // or contentUpdate { end_of_turn: true }
    if (message.serverContent) {
      const { serverContent } = message;
      // console.log("📄 [GEMINI] Server content received from Gemini:", serverContent);
      
      if ("interrupted" in serverContent) {
        console.log("⏸️ [GEMINI] Generation interrupted by Gemini");
        this.log("server.content", "interrupted");
        this.emit("interrupted");
        return;
      }
      if ("turnComplete" in serverContent) {
        console.log("✅ [GEMINI] Turn completed by Gemini");
        this.log("server.content", "turnComplete");
        this.emit("turncomplete");
      }

      if ("modelTurn" in serverContent) {
        // console.log("🎯 [GEMINI] Model turn received from Gemini");
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        // when its audio that is returned for modelTurn
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        // console.log(`🔊 [GEMINI] Processing ${audioParts.length} audio parts from Gemini`);

        // strip the audio parts out of the modelTurn
        const otherParts = difference(parts, audioParts);
        // console.log("otherParts", otherParts);

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);
          }
        });
        if (!otherParts.length) {
          return;
        }

        parts = otherParts;

        const content: { modelTurn: Content } = { modelTurn: { parts } };
        // console.log("📝 [GEMINI] Emitting content from Gemini:", content);
        this.emit("content", content);
        this.log(`server.content`, message);
      }
    } else {
      // console.log("❓ [GEMINI] Received unmatched message from Gemini:", message);
    }
  }

  /**
   * send realtimeInput, this is base64 chunks of "audio/pcm" and/or "image/jpg"
   */
  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    // console.log(`📤 [GEMINI] Sending realtime input to Gemini: ${chunks.length} chunks`);
    
    // Handle custom WebSocket for ephemeral tokens
    if (this._customWebSocket && this._customWebSocket.readyState === WebSocket.OPEN) {
      for (const ch of chunks) {
        const message = {
          realtime_input: {
            media_chunks: [ch]
          }
        };
        this._customWebSocket.send(JSON.stringify(message));
      }
      this.log(`client.realtimeInput`, `${chunks.length} chunks via custom WebSocket`);
      return;
    }
    
    // Handle normal SDK session
    let hasAudio = false;
    let hasVideo = false;
    for (const ch of chunks) {
      this.session?.sendRealtimeInput({ media: ch });
      if (ch.mimeType.includes("audio")) {
        hasAudio = true;
      }
      if (ch.mimeType.includes("image")) {
        hasVideo = true;
      }
      if (hasAudio && hasVideo) {
        break;
      }
    }
    const message =
      hasAudio && hasVideo
        ? "audio + video"
        : hasAudio
        ? "audio"
        : hasVideo
        ? "video"
        : "unknown";
    console.log(`📤 [GEMINI] Realtime input type: ${message}`);
    this.log(`client.realtimeInput`, message);
  }

  /**
   *  send a response to a function call and provide the id of the functions you are responding to
   */
  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      console.log(`🔧 [GEMINI] [${this.clientId}] Sending tool response to Gemini:`, toolResponse);
      console.log(`🔧 [GEMINI] [${this.clientId}] Response for functions:`, toolResponse.functionResponses.map(fr => fr.name));
      
      // Handle custom WebSocket for ephemeral tokens
      if (this._customWebSocket && this._customWebSocket.readyState === WebSocket.OPEN) {
        const message = {
          tool_response: {
            function_responses: toolResponse.functionResponses
          }
        };
        console.log("📤 [GEMINI] Sending tool response via custom WebSocket:", message);
        this._customWebSocket.send(JSON.stringify(message));
        this.log(`client.toolResponse`, toolResponse);
        return;
      }
      
      // Handle normal SDK session
      this.session?.sendToolResponse({
        functionResponses: toolResponse.functionResponses,
      });
      this.log(`client.toolResponse`, toolResponse);
    }
  }

  /**
   * send normal content parts such as { text }
   */
  send(parts: Part | Part[], turnComplete: boolean = true) {
    console.log(`📤 [GEMINI] Sending content to Gemini:`, {
      parts: Array.isArray(parts) ? parts : [parts],
      turnComplete
    });
    
    // Handle custom WebSocket for ephemeral tokens
    if (this._customWebSocket && this._customWebSocket.readyState === WebSocket.OPEN) {
      const message = {
        client_content: {
          turns: Array.isArray(parts) ? parts : [parts],
          turn_complete: turnComplete
        }
      };
      console.log("📤 [GEMINI] Sending via custom WebSocket:", message);
      this._customWebSocket.send(JSON.stringify(message));
      this.log(`client.send`, {
        turns: Array.isArray(parts) ? parts : [parts],
        turnComplete,
      });
      return;
    }
    
    // Handle normal SDK session
    this.session?.sendClientContent({ turns: parts, turnComplete });
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });
  }
}
